import { useState } from 'react'
import { CheckCircle2, Loader2, Plus, ScanLine, Ship, Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useReferenceIndex } from '@/features/service/hooks'
import {
  BoatPhotoPicker,
  type PendingBoatPhoto,
} from '@/features/inventory/components/boat-photo-picker'
import { ENGINE_MANUFACTURERS } from '@/features/inventory/boat-form'
import { UPLOAD_CONCURRENCY, uploadBoatImage } from '@/features/inventory/api'
import { downscaleImage } from '@/lib/image'
import { useDirtyClose } from '@/hooks/use-dirty-close'
import { useCreateInspection, useHINLookup } from '../hooks'
import { normalizeHIN } from '../api'
import { HinScanner } from './hin-scanner'

interface NewInspectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface FormState {
  hin: string
  year: string
  make: string
  model: string
  engineCount: string
  engineBrand: string
  engineModel: string
  engineSerial: string
  notes: string
}

const EMPTY_FORM: FormState = {
  hin: '',
  year: '',
  make: '',
  model: '',
  // Empty means "unset" so a matched boat's values can prefill; field()
  // falls back to the sensible defaults ('1' engines, 'none' brand).
  engineCount: '',
  engineBrand: '',
  engineModel: '',
  engineSerial: '',
  notes: '',
}

/**
 * The guided intake: HIN first (typed or scanned), everything auto-fills from
 * an exact inventory match or from the HIN itself (model year + USCG MIC
 * manufacturer), the inspector confirms engine details, picks photos, and
 * saves. Every inspection links to a boat — an unknown HIN creates a sparse
 * off-market boat automatically, so the photos have somewhere to go.
 */
export function NewInspectionDialog({ open, onOpenChange }: NewInspectionDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [photos, setPhotos] = useState<PendingBoatPhoto[]>([])
  const [scanOpen, setScanOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const normalizedHIN = normalizeHIN(form.hin)
  const lookup = useHINLookup(normalizedHIN)
  const createMutation = useCreateInspection()
  const { data: reference } = useReferenceIndex()
  const manufacturerOptions =
    reference?.manufacturers
      .filter((m) => m.key !== 'boat')
      .map((m) => ({ value: m.key, label: m.name })) ?? ENGINE_MANUFACTURERS

  // Auto-fill is derived, not applied: the lookup result prefills only the
  // fields the user hasn't typed into yet, so their own edits always win and
  // no effect/state-sync is needed. An exact inventory match prefills
  // everything; an unknown-but-valid HIN contributes the parsed model year
  // and the USCG MIC manufacturer as a make suggestion.
  const prefill: FormState = { ...EMPTY_FORM }
  if (lookup.data && normalizedHIN === lookup.data.hin) {
    if (lookup.data.matched && lookup.data.boat) {
      const boat = lookup.data.boat
      prefill.year = boat.year ? String(boat.year) : ''
      prefill.make = boat.make || ''
      prefill.model = boat.model || ''
      prefill.engineCount = boat.engine_count != null ? String(boat.engine_count) : ''
      prefill.engineBrand = boat.engine_manufacturer || 'none'
      prefill.engineModel = boat.engine_model || ''
      prefill.engineSerial = boat.engine_serial || ''
    } else {
      const { model_year: year, manufacturer } = lookup.data.derived
      prefill.year = year ? String(year) : ''
      prefill.make = manufacturer || ''
    }
  }
  // Effective field values: what the user typed, else the prefill.
  const field = (key: keyof FormState): string => {
    const fallback = key === 'engineCount' ? '1' : key === 'engineBrand' ? 'none' : ''
    return form[key] || prefill[key] || fallback
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const isDirty =
    form.hin !== '' ||
    form.make !== '' ||
    form.model !== '' ||
    form.year !== '' ||
    form.engineSerial !== '' ||
    form.engineModel !== '' ||
    form.notes !== '' ||
    photos.length > 0

  const reset = () => {
    setForm(EMPTY_FORM)
    for (const p of photos) URL.revokeObjectURL(p.previewUrl)
    setPhotos([])
    setUploadError(null)
  }

  const { guardOpenChange, discardGuard } = useDirtyClose(open, onOpenChange, isDirty)

  const close = (next: boolean) => {
    onOpenChange(next)
    if (!next) reset()
  }

  const addPhotos = (files: File[]) => {
    setPhotos((prev) => [
      ...prev,
      ...files.map((file) => {
        const pending: PendingBoatPhoto = {
          id: crypto.randomUUID(),
          file,
          previewUrl: URL.createObjectURL(file),
          processed: downscaleImage(file),
        }
        return pending
      }),
    ])
  }

  const removePhoto = (id: string) => {
    setPhotos((prev) => {
      const photo = prev.find((p) => p.id === id)
      if (photo) URL.revokeObjectURL(photo.previewUrl)
      return prev.filter((p) => p.id !== id)
    })
  }

  const canSubmit =
    normalizedHIN.length >= 12 &&
    field('year') !== '' &&
    field('make').trim() !== '' &&
    field('model').trim() !== ''

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return
    setSubmitting(true)
    setUploadError(null)
    try {
      const inspection = await createMutation.mutateAsync({
        hin: normalizedHIN,
        year: Number(field('year')),
        make: field('make').trim(),
        model: field('model').trim(),
        engine_count: field('engineCount') === '' ? 1 : Number(field('engineCount')),
        engine_brand: field('engineBrand') === 'none' ? '' : field('engineBrand'),
        engine_model: field('engineModel').trim(),
        engine_serial: field('engineSerial').trim(),
        notes: form.notes.trim(),
      })

      // Photos upload only after the inspection (and its boat) exist; a
      // failed upload must not lose the created record, so failures are
      // counted and surfaced after. First photo goes alone so it
      // deterministically wins the boat's cover.
      if (photos.length > 0) {
        let failed = 0
        const upload = async (photo: PendingBoatPhoto, sortOrder: number) => {
          try {
            await uploadBoatImage(inspection.boat_id, await photo.processed, sortOrder)
          } catch (err) {
            failed += 1
            console.error(`Failed to upload photo ${photo.file.name}:`, err)
          }
        }
        await upload(photos[0], 0)
        const rest = photos.slice(1)
        for (let i = 0; i < rest.length; i += UPLOAD_CONCURRENCY) {
          await Promise.all(
            rest.slice(i, i + UPLOAD_CONCURRENCY).map((p, j) => upload(p, i + j + 1)),
          )
        }
        if (failed > 0) {
          setUploadError(
            `${failed} of ${photos.length} photo(s) failed to upload — the inspection was saved; retry from the boat's gallery.`,
          )
        }
      }
      close(false)
    } catch (err) {
      console.error('Failed to create inspection:', err)
      setUploadError('Saving the inspection failed — nothing was recorded. Please retry.')
    } finally {
      setSubmitting(false)
    }
  }

  const lookupBanner = () => {
    if (!lookup.data || normalizeHIN(form.hin) !== lookup.data.hin) return null
    if (lookup.data.matched && lookup.data.boat) {
      const boat = lookup.data.boat
      return (
        <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm dark:border-emerald-900 dark:bg-emerald-950">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <div>
            <span className="font-medium">
              Matched in Inventory: {boat.year} {boat.make} {boat.model}
            </span>
            <p className="text-xs text-muted-foreground">
              Fields prefilled from the boat record — this inspection will link to it.
            </p>
          </div>
        </div>
      )
    }
    if (lookup.data.valid) {
      const { model_year: year, manufacturer, mic } = lookup.data.derived
      return (
        <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-900 dark:bg-blue-950">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <div>
            <span className="font-medium">New boat — this HIN isn&rsquo;t in Inventory yet.</span>
            <p className="text-xs text-muted-foreground">
              {year && <>Model year {year} decoded from the HIN. </>}
              {manufacturer ? (
                <>
                  MIC {mic} is registered to {manufacturer}.
                </>
              ) : (
                <>MIC {mic} is not in the USCG registry.</>
              )}{' '}
              A sparse off-market boat will be created for the photos.
            </p>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <>
      <Dialog open={open} onOpenChange={guardOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ship className="h-5 w-5" /> New Inspection
            </DialogTitle>
            <DialogDescription>
              Enter or scan the HIN — matching boats auto-fill. Engine details and photos complete
              the intake.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="hin">Hull Identification Number (HIN)</Label>
              <div className="flex gap-2">
                <Input
                  id="hin"
                  value={form.hin}
                  onChange={(e) => set('hin', e.target.value.toUpperCase())}
                  placeholder="e.g. YAM4B212J423 (transom, starboard side)"
                  maxLength={24}
                  className="font-mono tracking-wider"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setScanOpen(true)}
                  title="Scan the HIN plate from a photo"
                >
                  <ScanLine className="mr-2 h-4 w-4" /> Scan
                </Button>
              </div>
              {normalizedHIN.length > 0 && normalizedHIN.length < 12 && (
                <p className="text-xs text-muted-foreground">
                  HINs are 12 characters ({normalizedHIN.length}/12)
                </p>
              )}
              {lookup.isFetching && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Looking up…
                </p>
              )}
              {lookupBanner()}
            </div>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                <Input
                  id="year"
                  type="number"
                  min={1900}
                  max={2100}
                  value={field('year')}
                  onChange={(e) => set('year', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="make">Make</Label>
                <Input
                  id="make"
                  value={field('make')}
                  onChange={(e) => set('make', e.target.value)}
                  placeholder="Yamaha"
                />
              </div>
              <div className="space-y-2 lg:col-span-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={field('model')}
                  onChange={(e) => set('model', e.target.value)}
                  placeholder="242X"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <div className="space-y-2">
                <Label>Engine Count</Label>
                <Select value={field('engineCount')} onValueChange={(v) => set('engineCount', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['0', '1', '2', '3', '4'].map((n) => (
                      <SelectItem key={n} value={n}>
                        {n === '0' ? 'None' : n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Engine Brand</Label>
                <Select value={field('engineBrand')} onValueChange={(v) => set('engineBrand', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Unknown" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unknown / none</SelectItem>
                    {manufacturerOptions.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="engine-model">Engine Model</Label>
                <Input
                  id="engine-model"
                  value={field('engineModel')}
                  onChange={(e) => set('engineModel', e.target.value)}
                  placeholder="1.8L HO"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="engine-serial">Engine Serial</Label>
                <Input
                  id="engine-serial"
                  value={field('engineSerial')}
                  onChange={(e) => set('engineSerial', e.target.value)}
                  placeholder="6X6K-100432"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                placeholder="Condition observations, missing equipment, follow-ups…"
                className="h-20 resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label>Boat photos</Label>
              <BoatPhotoPicker photos={photos} onAdd={addPhotos} onRemove={removePhoto} />
              <p className="text-xs text-muted-foreground">
                Photos upload to the boat&rsquo;s gallery after you save.
              </p>
            </div>

            {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => guardOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!canSubmit || submitting}
            >
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Save Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {discardGuard}
      <HinScanner open={scanOpen} onOpenChange={setScanOpen} onHIN={(hin) => set('hin', hin)} />
    </>
  )
}
